import type {
  LeaderboardTriviaAnswerResult,
  LeaderboardTriviaPending,
  LeaderboardTriviaToday,
  TriviaAnswerLetter,
  TriviaOption,
} from '../../../domain/leaderboard/models/leaderboard-trivia.model';
import type { TriviaAnswerApiData, TriviaTodayApiData } from '../dtos/leaderboard-trivia-api.dto';

const LETTERS: TriviaAnswerLetter[] = ['A', 'B', 'C', 'D'];

function normalizeAnswerLetter(raw: string | undefined): TriviaAnswerLetter | null {
  const u = raw?.trim().toUpperCase();
  if (u === 'A' || u === 'B' || u === 'C' || u === 'D') {
    return u;
  }
  return null;
}

function buildOptions(data: TriviaTodayApiData): TriviaOption[] {
  const labels: Record<TriviaAnswerLetter, string | undefined> = {
    A: data.option_a,
    B: data.option_b,
    C: data.option_c,
    D: data.option_d,
  };
  return LETTERS.map((letter) => ({
    letter,
    label: labels[letter]?.trim() ?? '',
  })).filter((o) => o.label.length > 0);
}

export function mapTriviaTodayApiToDomain(data: TriviaTodayApiData | undefined): LeaderboardTriviaToday | null {
  if (data == null || typeof data.day !== 'number' || !Number.isFinite(data.day)) {
    return null;
  }

  if (data.already_answered) {
    return { kind: 'already_answered', day: data.day };
  }

  const question = data.question?.trim() ?? '';
  const options = buildOptions(data);
  const xp = data.xp_awarded;
  if (!question || options.length < 2 || xp == null || !Number.isFinite(xp)) {
    return null;
  }

  const trivia: LeaderboardTriviaPending = {
    day: data.day,
    xpAwarded: xp,
    question,
    options,
  };
  return { kind: 'pending', trivia };
}

export function mapTriviaAnswerApiToDomain(data: TriviaAnswerApiData | undefined): LeaderboardTriviaAnswerResult | null {
  if (data == null || typeof data.correct !== 'boolean' || typeof data.day !== 'number') {
    return null;
  }
  const xp = data.xp_awarded;
  if (xp == null || !Number.isFinite(xp)) {
    return null;
  }
  return {
    correct: data.correct,
    xpAwarded: xp,
    day: data.day,
    ...(data.xp_total_after != null && Number.isFinite(data.xp_total_after)
      ? { xpTotalAfter: data.xp_total_after }
      : {}),
  };
}

export { normalizeAnswerLetter };
